import React, { useEffect, useState, Component } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation, Outlet } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { Layout } from './components/Layout';
import { HomePage } from './pages/HomePage';
import { ProductsPage } from './pages/ProductsPage';
import { ProductDetailPage } from './pages/ProductDetailPage';
import { LoginPage, RegisterPage } from './pages/AuthPages';
import { AccountPage } from './pages/AccountPage';
import { AdminLoginPage, LeadsDashboardPage } from './pages/AdminLeadsPage';
import AdminCollectionsPage from './pages/AdminCollectionsPage';
import AdminProductsPage from './pages/AdminProductsPage';
import { B2BPage, B2BDashboardPage } from './pages/B2BPages';
import B2BApiKeysPage from './pages/B2BApiKeysPage';
import B2BDownloadsPage from './pages/B2BDownloadsPage';
import AdminSettingsPage from './pages/AdminSettingsPage';
import AdminProjectsPage from './pages/AdminProjectsPage';
import AdminJournalPage from './pages/AdminJournalPage';
import AdminAboutUsPage from './pages/AdminAboutUsPage';
import AdminContactsPage from './pages/AdminContactsPage';
import AdminAnalyticsPage from './pages/AdminAnalyticsPage';
import { ProjectsPage, ProjectDetailPage } from './pages/ProjectsPage';
import { BlogPage, BlogPostPage } from './pages/BlogPages';
import { ContactPage } from './pages/ContactPage';
import { WishlistPage, QuotePage } from './pages/WishlistQuotePages';
import { CustomFurniturePage } from './pages/CustomFurniturePage';
import { AboutPage } from './pages/AboutPage';

// ИМПОРТ ЮРИДИЧЕСКИХ СТРАНИЦ
import { PrivacyPage } from './pages/PrivacyPage';
import { TermsPage } from './pages/TermsPage';
import { CookiePage } from './pages/CookiePage';

import AIChatWidget from './components/AIChatWidget';
import LeadCaptureModal from './components/LeadCaptureModal';
import { useLanguageStore, useWishlistStore, useAuthStore, useAppearanceStore, useCopyStore } from './store';
import { initAnalytics } from './lib/analytics';
import { initRecaptcha } from './lib/recaptcha';
import { applyAppearanceToDom } from './lib/appearance';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AdminAppearancePage from './pages/AdminAppearancePage';
import AdminContentPage from './pages/AdminContentPage';
import AdminHeader from './components/admin/AdminHeader';
import { useTranslation } from './i18n/translations';
import './App.css';

const SUPPORTED_LANGUAGES = ['en', 'nl', 'ro', 'ru'];
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// 🎯 АВАРИЙНЫЙ ПЕРЕХВАТЧИК ОШИБОК REACT
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Critical Render Error Captured:', error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      const errText = typeof this.state.error === 'object' 
        ? (this.state.error?.message || JSON.stringify(this.state.error))
        : String(this.state.error || '');

      return (
        <div className="min-h-screen bg-white flex flex-col items-center justify-center p-8 text-center select-none">
          <div className="max-w-2xl w-full text-left border border-neutral-200 p-6 bg-neutral-50 shadow-sm space-y-4">
            <h2 className="text-lg font-medium text-red-600 uppercase tracking-wide">Atelier Application Notice</h2>
            <div className="p-3 bg-red-50 border border-red-200 rounded text-xs font-mono text-red-800 break-all">
              {errText || 'A temporary display issue occurred while rendering this page segment.'}
            </div>
            {this.state.errorInfo?.componentStack && (
              <div className="space-y-1">
                <span className="text-[11px] text-neutral-500 font-mono uppercase">Component Stack Trace:</span>
                <pre className="text-[10px] text-neutral-600 font-mono bg-white p-3 border border-neutral-200 overflow-auto max-h-48 whitespace-pre-wrap">
                  {this.state.errorInfo.componentStack}
                </pre>
              </div>
            )}
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null, errorInfo: null });
                window.location.reload();
              }}
              className="btn-primary text-xs uppercase tracking-widest px-6 py-3 bg-black text-white hover:bg-neutral-800 transition-colors"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function hexToHslChannels(hex) {
  if (!hex) return "0 0% 100%";
  let r = 0, g = 0, b = 0;
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex[1] + hex[2], 16);
    g = parseInt(hex[3] + hex[4], 16);
    b = parseInt(hex[5] + hex[6], 16);
  }
  r /= 255; g /= 255; b /= 255;
  let max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  if (max === min) {
    h = s = 0;
  } else {
    let d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

let isDesignSystemApplied = false;

(() => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  const map = {
    'home-studio-language': 'fedyk-language',
    'home-studio-auth': 'fedyk-auth',
    'home-studio-wishlist': 'fedyk-wishlist',
    'home-studio-quote': 'fedyk-quote',
  };
  for (const [oldKey, newKey] of Object.entries(map)) {
    const oldVal = localStorage.getItem(oldKey);
    if (oldVal && !localStorage.getItem(newKey)) {
      localStorage.setItem(newKey, oldVal);
    }
    if (oldVal) localStorage.removeItem(oldKey);
  }
})();

(() => {
  if (typeof window === 'undefined') return;
  try {
    const seg = window.location.pathname.split('/')[1];
    if (SUPPORTED_LANGUAGES.includes(seg)) {
      useLanguageStore.getState().setLanguage(seg);
      localStorage.setItem('user-language', seg);
    }
  } catch (e) { /* ignore */ }
})();

const GeoRedirect = () => {
  const savedLang = typeof window !== 'undefined' ? localStorage.getItem('user-language') : null;
  const defaultLang = SUPPORTED_LANGUAGES.includes(savedLang) ? savedLang : 'nl';
  return <Navigate to={`/${defaultLang}`} replace />;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
};

const LanguageRoute = ({ children }) => {
  const { lang } = useParams();
  const isSupported = SUPPORTED_LANGUAGES.includes(lang);

  useEffect(() => {
    if (isSupported) {
      const currentLang = useLanguageStore.getState().language;
      if (currentLang !== lang) {
        useLanguageStore.getState().setLanguage(lang);
        try { localStorage.setItem('user-language', lang); } catch (e) { /* ignore */ }
      }
    }
  }, [lang, isSupported]);

  if (!isSupported) {
    return <Navigate to="/en" replace />;
  }

  return children;
};

const AppInit = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  const { fetchWishlist } = useWishlistStore();
  
  const [isInitialized, setIsInitialized] = useState(() => {
    try {
      const cachedApp = localStorage.getItem('fedyk-appearance-cache');
      const cachedCopy = localStorage.getItem('fedyk-copy-cache');
      return !!(cachedApp && cachedCopy);
    } catch (e) {
      return false;
    }
  });

  const config = useAppearanceStore((s) => s.config);
  const fetchAppearance = useAppearanceStore((s) => s.fetchAppearance);
  const fetchCopy = useCopyStore((s) => s.fetchCopy);
  const isCopyLoaded = useCopyStore((s) => s.loaded);

  useEffect(() => {
    const fallbackTimer = setTimeout(() => {
      setIsInitialized(true);
    }, 1500);
    return () => clearTimeout(fallbackTimer);
  }, []);

  useEffect(() => {
    initAnalytics();
    initRecaptcha();
    fetchAppearance();
    fetchCopy();

    const handleCopyPublish = () => { fetchCopy(); };
    window.addEventListener('site-copy-updated', handleCopyPublish);
    return () => window.removeEventListener('site-copy-updated', handleCopyPublish);
  }, [fetchAppearance, fetchCopy]);

  useEffect(() => {
    if (config) {
      applyAppearanceToDom(config);
    }
  }, [config]);

  useEffect(() => {
    const handleThemePublish = () => { fetchAppearance(); };
    window.addEventListener('site-appearance-updated', handleThemePublish);
    return () => window.removeEventListener('site-appearance-updated', handleThemePublish);
  }, [fetchAppearance]);

  useEffect(() => {
    if (isDesignSystemApplied) return;

    fetch(`${API_URL}/api/public/appearance`)
      .then(res => res.json())
      .then(data => {
        const payload = data?.appearance || data?.published || data;
        if (payload && payload.colors) {
          const root = document.documentElement;
          
          root.style.setProperty('--background', hexToHslChannels(payload.colors.background));
          root.style.setProperty('--card', hexToHslChannels(payload.colors.secondary || payload.colors.background));
          root.style.setProperty('--foreground', hexToHslChannels(payload.colors.foreground));
          root.style.setProperty('--primary', hexToHslChannels(payload.colors.primary));
          root.style.setProperty('--border', hexToHslChannels(payload.colors.border));

          root.style.setProperty('--cms-text-h1-h2', payload.colors.primary || '#141414');
          root.style.setProperty('--cms-text-h3-h4', payload.colors.foreground || '#141414');
          root.style.setProperty('--cms-text-body-main', payload.colors.foreground || '#141414');
          root.style.setProperty('--cms-text-body-muted', payload.colors.muted_foreground || '#737373');
          root.style.setProperty('--cms-text-links', payload.colors.primary || '#141414');
          root.style.setProperty('--cms-text-on-dark', payload.colors.primary_foreground || '#FFFFFF');

          root.style.setProperty('--cms-bg-canvas', payload.colors.background || '#ffffff');
          root.style.setProperty('--cms-bg-navbar', payload.colors.background || '#ffffff');
          root.style.setProperty('--cms-bg-cards', payload.colors.secondary || '#f5f5f5');
          root.style.setProperty('--cms-bg-dark-modules', payload.colors.primary || '#141414');
          root.style.setProperty('--cms-bg-footer', payload.colors.button_bg || '#141414');
          root.style.setProperty('--cms-border-divider', payload.colors.border || '#e5e5e5');
          
          isDesignSystemApplied = true;
        }
      })
      .catch(err => console.error("CMS Design Ingest Outage Fallback:", err));
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchWishlist();
    }
  }, [isAuthenticated, fetchWishlist]);

  useEffect(() => {
    if (isCopyLoaded && config) {
      setIsInitialized(true);
    }
  }, [isCopyLoaded, config]);

  useEffect(() => {
    const backgroundGeoCheck = async () => {
      if (!localStorage.getItem('user-country')) {
        try {
          const res = await fetch('https://ipapi.co/json/');
          const geo = await res.json();
          if (geo.country_code) {
            let code = geo.country_code.toUpperCase();
            if (code === 'NL' || code === 'BE') code = 'BE';
            localStorage.setItem('user-country', code);
            window.dispatchEvent(new Event('user-country-resolved'));
          }
        } catch (e) {
          localStorage.setItem('user-country', 'BE');
        }
      }
    };

    if (isInitialized) {
      backgroundGeoCheck();
    }
  }, [isInitialized]);

  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center text-neutral-500 text-xs tracking-[0.3em] uppercase select-none">
        <div className="animate-pulse">Connecting Atelier…</div>
      </div>
    );
  }

  return children;
};

const CookieConsent = () => {
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) setShow(true);
  }, []);

  const accept = () => {
    localStorage.setItem('cookie-consent', 'accepted');
    setShow(false);
  };

  if (!show) return null;

  const acceptLabel = typeof t?.common?.accept === 'string' 
    ? t.common.accept 
    : (t?.common?.accept?.[language] || 'Accept');

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-foreground text-background p-6 select-none" data-testid="cookie-consent">
      <div className="container-custom flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="text-sm font-light">We use cookies to enhance your experience.</p>
        <div className="flex gap-4">
          <button onClick={accept} className="btn-primary bg-background text-foreground text-xs rounded-none border-none cursor-pointer">
            {acceptLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminLayout = () => {
  const location = useLocation();
  const segments = location.pathname.split('/');
  const activeSection = segments[segments.length - 1] || 'leads';

  return (
    <>
      <AdminHeader active={activeSection} />
      <div className="cms-admin-content-container">
        <Outlet />
      </div>
    </>
  );
};

const NotFoundPage = () => {
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);

  const homeLabel = typeof t?.common?.returnHome === 'string' 
    ? t.common.returnHome 
    : (t?.common?.returnHome?.[language] || 'Return Home');

  return (
    <div className="min-h-[60vh] flex items-center justify-center select-none">
      <div className="text-center">
        <h1 className="text-display mb-4">404</h1>
        <div onClick={() => window.location.href = `/${language}`} className="btn-primary cursor-pointer">
          {homeLabel}
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AppInit>
          <ScrollToTop />
          <Toaster position="top-right" />
          <CookieConsent />
          <AIChatWidget />
          <LeadCaptureModal />
          <Routes>
            <Route path="/" element={<GeoRedirect />} />
            <Route path="/:lang/login" element={<LanguageRoute><LoginPage /></LanguageRoute>} />
            <Route path="/:lang/register" element={<LanguageRoute><RegisterPage /></LanguageRoute>} />
            <Route path="/:lang/forgot-password" element={<LanguageRoute><ForgotPassword /></LanguageRoute>} />
            <Route path="/:lang/reset-password" element={<LanguageRoute><ResetPassword /></LanguageRoute>} />

            <Route path="/admin" element={<AdminLoginPage />} />
            
            <Route element={<AdminLayout />}>
              <Route path="/admin/leads" element={<LeadsDashboardPage />} />
              <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />
              <Route path="/admin/collections" element={<AdminCollectionsPage />} />
              <Route path="/admin/products" element={<AdminProductsPage />} />
              <Route path="/admin/projects" element={<AdminProjectsPage />} />
              <Route path="/admin/journal" element={<AdminJournalPage />} />
              <Route path="/admin/about" element={<AdminAboutUsPage />} />
              <Route path="/admin/contacts" element={<AdminContactsPage />} />
              <Route path="/admin/settings" element={<AdminSettingsPage />} />
              <Route path="/admin/appearance" element={<AdminAppearancePage />} />
              <Route path="/admin/content" element={<AdminContentPage />} />
            </Route>
              
            <Route path="/:lang" element={<LanguageRoute><Layout /></LanguageRoute>}>
              <Route index element={<HomePage />} />
              <Route path="products" element={<ProductsPage />} />
              <Route path="products/:id" element={<ProductDetailPage />} />
              <Route path="about" element={<AboutPage />} />
              <Route path="account" element={<AccountPage />} />
              <Route path="b2b" element={<B2BPage />} />
              <Route path="b2b/dashboard" element={<B2BDashboardPage />} />
              <Route path="b2b/downloads" element={<B2BDownloadsPage />} />
              <Route path="b2b/api-keys" element={<B2BApiKeysPage />} />
              <Route path="projects" element={<ProjectsPage />} />
              <Route path="projects/:slug" element={<ProjectDetailPage />} />
              <Route path="blog" element={<BlogPage />} />
              <Route path="blog/:slug" element={<BlogPostPage />} />
              <Route path="contact" element={<ContactPage />} />
              <Route path="wishlist" element={<WishlistPage />} />
              <Route path="quote" element={<QuotePage />} />
              <Route path="custom-furniture" element={<CustomFurniturePage />} />
              
              {/* ПОЛНОЦЕННЫЕ ЮРИДИЧЕСКИЕ СТРАНИЦЫ ВМЕСТО ЗАГЛУШЕК */}
              <Route path="privacy" element={<PrivacyPage />} />
              <Route path="terms" element={<TermsPage />} />
              <Route path="cookies" element={<CookiePage />} />
              
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </AppInit>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
